import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandPointer } from "@fortawesome/free-solid-svg-icons";

const VisiGiftScratch = () => {
  const canvasRef = useRef(null);
  const [isScratched, setIsScratched] = useState(false);
  const [win, setWin] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    // Random win or lose
    const isWin = Math.random() < 0.3; // 30% win rate
    setWin(isWin);
    setMessage(isWin ? "🎉 You Have Won Gift!" : "😔 Sorry Try Again");

    // Canvas setup
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      drawOverlay();
    };

    const drawOverlay = () => {
      const width = canvas.width;
      const height = canvas.height;
      // Clear and draw underlying message first
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = isWin ? "#10b981" : "#ef4444"; // Green for win, red for lose
      ctx.font = "bold 22px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(message, width / 2, height / 2);

      // Then draw scratch overlay on top
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#8b5cf6"; // Purple overlay
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "#e0e0ff";
      ctx.font = "bold 22px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Scratch Here 🎁", width / 2, height / 2);
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    let drawing = false;
    let lastX = 0;
    let lastY = 0;
    const brushSize = 35;

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDrawing = (e) => {
      if (e.preventDefault) e.preventDefault();
      drawing = true;
      const { x, y } = getPos(e);
      lastX = x;
      lastY = y;
      drawScratch(x, y); // Draw initial point
    };

    const stopDrawing = (e) => {
      if (e.preventDefault) e.preventDefault();
      drawing = false;
      checkScratchCompletion();
    };

    const drawScratch = (x, y) => {
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(x, y, brushSize, 0, Math.PI * 2);
      ctx.fill();

      // Draw connecting line if moving
      if (lastX !== null && lastY !== null) {
        ctx.lineWidth = brushSize * 2;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(lastX, lastY);
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      lastX = x;
      lastY = y;
    };

    const scratch = (e) => {
      if (!drawing) return;
      if (e.preventDefault) e.preventDefault();
      const { x, y } = getPos(e);
      drawScratch(x, y);
    };

    const checkScratchCompletion = () => {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      let transparent = 0;

      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] < 128) transparent++;
      }

      const scratched = transparent / (pixels.length / 4);
      if (scratched > 0.3) {
        setIsScratched(true);
        // Final reveal: clear overlay completely
        ctx.globalCompositeOperation = "source-over";
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = isWin ? "#10b981" : "#ef4444";
        ctx.font = "bold 22px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(message, canvas.width / 2, canvas.height / 2);
      }
    };

    // Add event listeners
    const opts = { passive: false };
    canvas.addEventListener("mousedown", startDrawing, opts);
    canvas.addEventListener("mousemove", scratch, opts);
    canvas.addEventListener("mouseup", stopDrawing, opts);
    canvas.addEventListener("touchstart", startDrawing, opts);
    canvas.addEventListener("touchmove", scratch, opts);
    canvas.addEventListener("touchend", stopDrawing, opts);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      const opts = { passive: false };
      canvas.removeEventListener("mousedown", startDrawing, opts);
      canvas.removeEventListener("mousemove", scratch, opts);
      canvas.removeEventListener("mouseup", stopDrawing, opts);
      canvas.removeEventListener("touchstart", startDrawing, opts);
      canvas.removeEventListener("touchmove", scratch, opts);
      canvas.removeEventListener("touchend", stopDrawing, opts);
    };
  }, [win, message, isScratched]);

  return (
    <div className="min-h-screen flex flex-col bg-[#1a0b1e] text-purple-100 font-sans">
      {/* Header */}
      <header className="bg-[#2a0f2f] shadow-sm border-b border-purple-800 px-4 py-3 sticky top-0 z-10">
        <div className="flex flex-col items-center text-center">
          <h1 className="text-xl font-bold text-purple-400">VisiGift Scratch Card</h1>
          <p className="text-sm text-purple-300 mt-1">Scratch to reveal your reward!</p>
        </div>
      </header>

      {/* Main */}
      <main className="flex-grow px-4 py-6 flex flex-col items-center justify-center">
        <div className="max-w-md w-full space-y-4">
          {/* Instruction */}
          <div className="text-center">
            <FontAwesomeIcon
              icon={faHandPointer}
              className="text-3xl text-purple-400 mx-auto mb-2"
            />
            <p className="text-sm text-purple-300">
              Use your finger or mouse to scratch the card below
            </p>
          </div>

          {/* Scratch card */}
          <div className="relative rounded-lg overflow-hidden shadow-lg border border-purple-800">
            <canvas
              ref={canvasRef}
              className="w-full h-64 block"
              style={{
                touchAction: "none",
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
              }}
            />
          </div>

          {/* CTA */}
          <Link
            to="/visigift-home"
            className="w-full block bg-purple-600 text-white py-3 px-4 rounded-md text-center font-medium hover:bg-purple-700 transition-colors"
          >
            Try Another Scratch
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a0f2f] border-t border-purple-800 text-purple-300 py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-purple-400">
              About
            </Link>
            <Link to="/privacy-policy" className="text-white hover:text-purple-400">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="text-white hover:text-purple-400">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="text-white hover:text-purple-400">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VisiGiftScratch;